/**
 * Importación de Ventas x Mayor desde otros sistemas.
 *
 * Dos plantillas Excel:
 *   - Plantilla de ventas (datos generales): Factura, Cliente, Rnc, Ubicacion,
 *     Telefono, Fecha inicio, Cuotas, Valor total, Valor pagado.
 *   - Plantilla de detalle (chasis): Nombre cliente, Factura, fecha, Chasis,
 *     Marca, Modelo, Color, Costo, Precio, Precio Min.
 *
 * Flujo: subir ambos archivos -> "Validar" llama /api/xmayor_importar_validar
 * (dry-run en el servidor: cruza plantillas, clientes por RNC, chasis contra
 * inventario) y muestra errores/avisos/vista previa -> "Importar" llama
 * /api/xmayor_importar_guardar, que revalida y graba TODO en una transacción
 * (si algo falla no se importa nada).
 *
 * El parseo del Excel es en el navegador (mismo excelPreview.js de la
 * importación de préstamos); el backend recibe las filas como JSON.
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { DataGrid } from "@mui/x-data-grid";
import MDButton from "components/MDButton";
import { FP, PageHeader, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { parseExcelPreview, downloadTemplateXlsx } from "modules/importacion/excelPreview";

const PLANTILLAS = {
  ventas: {
    titulo: "Plantilla de ventas",
    descripcion: "Datos generales: una fila por factura.",
    columnas: [
      "Factura",
      "Cliente",
      "Rnc",
      "Ubicacion",
      "Telefono",
      "Fecha inicio",
      "Cuotas",
      "Valor total",
      "Valor pagado",
    ],
    archivo: "plantilla_ventas_xmayor.xlsx",
  },
  detalles: {
    titulo: "Plantilla de detalle",
    descripcion: "Detalle por chasis: una fila por vehículo.",
    columnas: [
      "Nombre cliente",
      "Factura",
      "fecha",
      "Chasis",
      "Marca",
      "Modelo",
      "Color",
      "Costo",
      "Precio",
      "Precio Min",
    ],
    archivo: "plantilla_detalle_factura_xmayor.xlsx",
  },
};

// El backend normaliza encabezados sin distinguir mayúsculas/espacios, pero
// aquí se exige la plantilla exacta para atajar archivos equivocados temprano.
function normalizarEncabezado(valor) {
  return String(valor ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

function encabezadosCoinciden(actuales, esperados) {
  if (!Array.isArray(actuales) || actuales.length !== esperados.length) return false;
  return esperados.every((e, i) => normalizarEncabezado(actuales[i]) === normalizarEncabezado(e));
}

function archivoVacio() {
  return { fileName: null, rows: [], rowCount: 0, error: null, loading: false };
}

const fmtMoneda = new Intl.NumberFormat("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// El theme de Material Dashboard pone TableHead en display:block (para sus
// tablas propias), lo que despega el encabezado de las filas del cuerpo;
// esto restaura el layout estándar de tabla en las tablas del diálogo.
const tablaHeadSx = { display: "table-header-group", p: 0, borderRadius: 0 };

export default function ImportarXmayor() {
  const { post } = useApi();

  const [archivos, setArchivos] = useState({ ventas: archivoVacio(), detalles: archivoVacio() });
  const [validacion, setValidacion] = useState(null); // respuesta de /validar
  const [validando, setValidando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState("");
  const [confirmarOpen, setConfirmarOpen] = useState(false);
  const [resultado, setResultado] = useState(null); // respuesta de /guardar
  const [detalle, setDetalle] = useState(null); // factura seleccionada en la vista previa

  const inputVentasRef = useRef(null);
  const inputDetallesRef = useRef(null);
  const refs = { ventas: inputVentasRef, detalles: inputDetallesRef };

  const handleArchivo = useCallback(async (clave, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    // Cualquier cambio de archivo invalida la validación y el resultado previos.
    setValidacion(null);
    setDetalle(null);
    setResultado(null);
    setErrorGeneral("");
    setArchivos((prev) => ({ ...prev, [clave]: { ...prev[clave], loading: true, error: null } }));
    try {
      // maxPreviewRows alto: aquí las filas parseadas SON las que se importan.
      const r = await parseExcelPreview(file, { maxPreviewRows: 100000 });
      const okHeaders = encabezadosCoinciden(r.headers, PLANTILLAS[clave].columnas);
      setArchivos((prev) => ({
        ...prev,
        [clave]: {
          fileName: r.fileName,
          rows: okHeaders ? r.rows.map(({ id, ...resto }) => resto) : [],
          rowCount: r.rowCount,
          loading: false,
          error: okHeaders
            ? null
            : `Las columnas no coinciden con la ${PLANTILLAS[clave].titulo.toLowerCase()}. ` +
              `Deben ser exactamente: ${PLANTILLAS[clave].columnas.join(", ")}.`,
        },
      }));
    } catch (err) {
      setArchivos((prev) => ({
        ...prev,
        [clave]: { ...archivoVacio(), error: err?.message || "Error al leer el archivo." },
      }));
    }
  }, []);

  const handleQuitar = useCallback((clave) => {
    setArchivos((prev) => ({ ...prev, [clave]: archivoVacio() }));
    setValidacion(null);
    setDetalle(null);
    setResultado(null);
    setErrorGeneral("");
  }, []);

  const listosParaValidar =
    archivos.ventas.rows.length > 0 &&
    archivos.detalles.rows.length > 0 &&
    !archivos.ventas.error &&
    !archivos.detalles.error;

  const handleValidar = useCallback(async () => {
    setValidando(true);
    setErrorGeneral("");
    setValidacion(null);
    setDetalle(null);
    setResultado(null);
    try {
      const { ok, data } = await post("/api/xmayor_importar_validar", {
        ventas: archivos.ventas.rows,
        detalles: archivos.detalles.rows,
      });
      if (!ok || data?.Error) {
        throw new Error(data?.Error || "No se pudo validar la importación.");
      }
      setValidacion(data);
    } catch (e) {
      setErrorGeneral(e?.message || "No se pudo validar la importación.");
    } finally {
      setValidando(false);
    }
  }, [post, archivos]);

  const handleImportar = useCallback(async () => {
    setConfirmarOpen(false);
    setImportando(true);
    setErrorGeneral("");
    try {
      const { ok, data } = await post("/api/xmayor_importar_guardar", {
        ventas: archivos.ventas.rows,
        detalles: archivos.detalles.rows,
      });
      if (!ok || data?.Error) {
        const detalle = Array.isArray(data?.errores) && data.errores.length
          ? " " + data.errores.join(" | ")
          : "";
        throw new Error((data?.Error || "No se pudo importar.") + detalle);
      }
      setResultado(data);
      setValidacion(null);
      setDetalle(null);
      setArchivos({ ventas: archivoVacio(), detalles: archivoVacio() });
    } catch (e) {
      setErrorGeneral(e?.message || "No se pudo importar.");
    } finally {
      setImportando(false);
    }
  }, [post, archivos]);

  const columnasPrevia = useMemo(
    () => [
      { field: "factura", headerName: "Factura origen", width: 120 },
      { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 180 },
      { field: "rnc", headerName: "RNC", width: 120 },
      {
        field: "clienteExistente",
        headerName: "Cliente en sistema",
        width: 140,
        renderCell: (p) =>
          p.value ? (
            <Chip size="small" label={`Existente #${p.value}`} />
          ) : (
            <Chip size="small" color="info" label="Se creará" />
          ),
      },
      { field: "tipofactura", headerName: "Tipo", width: 100 },
      { field: "fecha", headerName: "Fecha", width: 110 },
      { field: "cuotas", headerName: "Cuotas", width: 80, align: "right", headerAlign: "right" },
      {
        field: "total", headerName: "Total", width: 120, align: "right", headerAlign: "right",
        renderCell: (p) => fmtMoneda.format(p.value || 0),
      },
      {
        field: "pagado", headerName: "Pagado", width: 120, align: "right", headerAlign: "right",
        renderCell: (p) => fmtMoneda.format(p.value || 0),
      },
      {
        field: "balance", headerName: "Balance", width: 120, align: "right", headerAlign: "right",
        renderCell: (p) => fmtMoneda.format(p.value || 0),
      },
      {
        field: "chasis",
        headerName: "Chasis",
        width: 130,
        renderCell: (p) => {
          const nuevos = (p.value || []).filter((c) => c.nuevo).length;
          return `${(p.value || []).length}${nuevos ? ` (${nuevos} nuevos)` : ""}`;
        },
      },
    ],
    []
  );

  const columnasResultado = useMemo(
    () => [
      { field: "factura", headerName: "Factura origen", width: 130 },
      { field: "id", headerName: "Factura interna", width: 130 },
      { field: "cliente", headerName: "Cliente #", width: 110 },
      { field: "chasis", headerName: "Chasis", width: 90, align: "right", headerAlign: "right" },
      {
        field: "total", headerName: "Total", width: 130, align: "right", headerAlign: "right",
        renderCell: (p) => fmtMoneda.format(p.value || 0),
      },
      {
        field: "pagado", headerName: "Pagado", width: 130, align: "right", headerAlign: "right",
        renderCell: (p) => fmtMoneda.format(p.value || 0),
      },
    ],
    []
  );

  const resumen = validacion?.resumen;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Importar Ventas x Mayor"
        subtitle="Importa facturas al por mayor de otro sistema desde las dos plantillas Excel"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {["ventas", "detalles"].map((clave) => (
          <input
            key={clave}
            ref={refs[clave]}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            style={{ display: "none" }}
            onChange={(e) => handleArchivo(clave, e)}
          />
        ))}

        {/* Paso 1: archivos */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {["ventas", "detalles"].map((clave) => {
            const plantilla = PLANTILLAS[clave];
            const estado = archivos[clave];
            return (
              <Grid item xs={12} md={6} key={clave}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    {plantilla.titulo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {plantilla.descripcion} Columnas: {plantilla.columnas.join(", ")}.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    <MDButton
                      variant="outlined"
                      color="dark"
                      size="small"
                      startIcon={<DownloadOutlinedIcon />}
                      onClick={() => downloadTemplateXlsx(plantilla.archivo, plantilla.columnas)}
                    >
                      Plantilla
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="info"
                      size="small"
                      startIcon={<UploadFileOutlinedIcon />}
                      disabled={estado.loading || importando}
                      onClick={() => refs[clave].current?.click()}
                    >
                      {estado.fileName ? "Cambiar archivo" : "Elegir archivo"}
                    </MDButton>
                    {estado.fileName && (
                      <MDButton variant="outlined" color="dark" size="small" onClick={() => handleQuitar(clave)}>
                        Quitar
                      </MDButton>
                    )}
                    {estado.fileName && <Chip label={estado.fileName} variant="outlined" size="small" />}
                    {estado.rowCount > 0 && !estado.error && (
                      <Chip label={`${estado.rowCount} fila${estado.rowCount === 1 ? "" : "s"}`} color="primary" size="small" />
                    )}
                    {estado.loading && <CircularProgress size={20} />}
                  </Stack>
                  {estado.error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {estado.error}
                    </Alert>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Paso 2: validar */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<FactCheckOutlinedIcon />}
            disabled={!listosParaValidar || validando || importando}
            onClick={handleValidar}
          >
            {validando ? "Validando..." : "Validar archivos"}
          </MDButton>
          {validacion?.valido && (
            <MDButton
              variant="gradient"
              color="success"
              disabled={importando}
              onClick={() => setConfirmarOpen(true)}
            >
              {importando ? "Importando..." : "Importar"}
            </MDButton>
          )}
          {importando && <CircularProgress size={22} />}
        </Stack>

        {errorGeneral && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorGeneral}
          </Alert>
        )}

        {/* Resultado de la validación */}
        {validacion && (
          <>
            <Alert severity={validacion.valido ? "success" : "error"} sx={{ mb: 2 }}>
              <AlertTitle>
                {validacion.valido
                  ? "Validación correcta: todo listo para importar"
                  : "La importación tiene errores; corrija el Excel y vuelva a cargarlo"}
              </AlertTitle>
              {resumen && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  <Chip size="small" label={`${resumen.facturas} facturas`} />
                  <Chip size="small" label={`${resumen.facturasCredito} crédito / ${resumen.facturasContado} contado`} />
                  <Chip size="small" label={`${resumen.chasis} chasis (${resumen.chasisNuevos} nuevos)`} />
                  <Chip size="small" label={`${resumen.clientesNuevos} clientes nuevos`} />
                  <Chip size="small" label={`Total ${fmtMoneda.format(resumen.montoTotal || 0)}`} />
                  <Chip size="small" label={`Pagado ${fmtMoneda.format(resumen.montoPagado || 0)}`} />
                </Stack>
              )}
            </Alert>

            {validacion.errores?.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Errores ({validacion.errores.length})</AlertTitle>
                {validacion.errores.map((e, i) => (
                  <Typography key={`err-${i}`} variant="body2">
                    • {e}
                  </Typography>
                ))}
              </Alert>
            )}

            {validacion.avisos?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Avisos ({validacion.avisos.length})</AlertTitle>
                {validacion.avisos.map((a, i) => (
                  <Typography key={`avi-${i}`} variant="body2">
                    • {a}
                  </Typography>
                ))}
              </Alert>
            )}

            {validacion.facturas?.length > 0 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Pulse una factura para ver su detalle completo (cliente, chasis y plan de cuotas)
                  antes de importar.
                </Typography>
                <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
                  <DataGrid
                    autoHeight
                    rows={validacion.facturas.map((f, i) => ({ ...f, _rid: i }))}
                    getRowId={(r) => r._rid}
                    columns={columnasPrevia}
                    density="compact"
                    disableRowSelectionOnClick
                    onRowClick={(params) => setDetalle(params.row)}
                    sx={{ ...fpDataGridSx, "& .MuiDataGrid-row": { cursor: "pointer" } }}
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  />
                </Paper>
              </>
            )}
          </>
        )}

        {/* Resultado de la importación */}
        {resultado && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>{resultado.ok}</AlertTitle>
              {resultado.clientesCreados > 0 && (
                <Typography variant="body2">Clientes creados: {resultado.clientesCreados}.</Typography>
              )}
              <Typography variant="body2">
                Cada factura conserva su número de origen como referencia y recibió un número interno nuevo.
              </Typography>
            </Alert>
            {resultado.facturas?.length > 0 && (
              <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
                <DataGrid
                  autoHeight
                  rows={resultado.facturas.map((f, i) => ({ ...f, _rid: i }))}
                  getRowId={(r) => r._rid}
                  columns={columnasResultado}
                  density="compact"
                  disableRowSelectionOnClick
                  sx={fpDataGridSx}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                />
              </Paper>
            )}
          </>
        )}
      </Box>

      {/* Detalle de una factura de la vista previa (como el modal de la
          importación de préstamos): cliente, chasis y plan de cuotas tal
          cual se van a grabar. */}
      <Dialog open={Boolean(detalle)} onClose={() => setDetalle(null)} maxWidth="md" fullWidth>
        {detalle && (
          <>
            <DialogTitle>
              Factura {detalle.factura} — {detalle.cliente}
            </DialogTitle>
            <DialogContent dividers>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip size="small" label={detalle.tipofactura} color={detalle.tipofactura === "Credito" ? "warning" : "default"} />
                <Chip size="small" label={`Fecha ${detalle.fecha || "-"}`} />
                <Chip size="small" label={`RNC ${detalle.rnc}`} />
                {detalle.clienteExistente ? (
                  <Chip size="small" label={`Cliente existente #${detalle.clienteExistente}`} />
                ) : (
                  <Chip size="small" color="info" label="Cliente se creará" />
                )}
              </Stack>

              {!detalle.clienteExistente && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Se creará el cliente <strong>{detalle.cliente}</strong> con RNC {detalle.rnc}
                  {detalle.ubicacion ? `, dirección "${detalle.ubicacion}"` : ", sin dirección"}
                  {detalle.telefono ? ` y teléfono ${detalle.telefono}.` : " y sin teléfono."}
                </Alert>
              )}

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip size="small" variant="outlined" label={`Total ${fmtMoneda.format(detalle.total || 0)}`} />
                <Chip size="small" variant="outlined" label={`Pagado ${fmtMoneda.format(detalle.pagado || 0)}`} />
                <Chip
                  size="small"
                  color={detalle.balance > 0 ? "warning" : "success"}
                  label={detalle.balance > 0 ? `Balance ${fmtMoneda.format(detalle.balance)}` : "Saldada"}
                />
              </Stack>

              <Typography variant="subtitle2" gutterBottom>
                Chasis ({detalle.chasis?.length || 0})
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ mb: 2, border: 1, borderColor: "divider" }}>
                <Table size="small">
                  <TableHead sx={tablaHeadSx}>
                    <TableRow>
                      <TableCell>Chasis</TableCell>
                      <TableCell>Marca</TableCell>
                      <TableCell>Modelo</TableCell>
                      <TableCell>Color</TableCell>
                      <TableCell align="right">Costo</TableCell>
                      <TableCell align="right">Precio</TableCell>
                      <TableCell align="right">Precio Min</TableCell>
                      <TableCell>Inventario</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detalle.chasis || []).map((c) => (
                      <TableRow key={c.chasis}>
                        <TableCell>{c.chasis}</TableCell>
                        <TableCell>{c.marca || "-"}</TableCell>
                        <TableCell>{c.modelo || "-"}</TableCell>
                        <TableCell>{c.color || "-"}</TableCell>
                        <TableCell align="right">{fmtMoneda.format(c.costo || 0)}</TableCell>
                        <TableCell align="right">{fmtMoneda.format(c.precio || 0)}</TableCell>
                        <TableCell align="right">{fmtMoneda.format(c.precioMin || 0)}</TableCell>
                        <TableCell>
                          {c.nuevo ? (
                            <Chip size="small" color="info" label="Se creará" />
                          ) : (
                            <Chip size="small" label="Existente" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {detalle.planCuotas?.length > 0 ? (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Plan de cuotas ({detalle.planCuotas.length})
                  </Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: "divider" }}>
                    <Table size="small">
                      <TableHead sx={tablaHeadSx}>
                        <TableRow>
                          <TableCell align="right">#</TableCell>
                          <TableCell>Fecha</TableCell>
                          <TableCell align="right">Valor</TableCell>
                          <TableCell align="right">Pagado</TableCell>
                          <TableCell>Estado</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detalle.planCuotas.map((q) => (
                          <TableRow key={q.numero}>
                            <TableCell align="right">{q.numero}</TableCell>
                            <TableCell>{q.fecha}</TableCell>
                            <TableCell align="right">{fmtMoneda.format(q.valor || 0)}</TableCell>
                            <TableCell align="right">{fmtMoneda.format(q.pagado || 0)}</TableCell>
                            <TableCell>
                              {q.saldada ? (
                                <Chip size="small" color="success" label="Saldada" />
                              ) : q.pagado > 0 ? (
                                <Chip size="small" color="warning" label="Parcial" />
                              ) : (
                                <Chip size="small" variant="outlined" label="Pendiente" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Alert severity="info">
                  Venta de contado: se graba saldada, sin plan de cuotas.
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <MDButton variant="outlined" color="dark" onClick={() => setDetalle(null)}>
                Cerrar
              </MDButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Confirmación antes de grabar */}
      <Dialog open={confirmarOpen} onClose={() => setConfirmarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar importación</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se importarán {resumen?.facturas ?? 0} facturas con {resumen?.chasis ?? 0} chasis
            {resumen?.clientesNuevos ? ` y se crearán ${resumen.clientesNuevos} clientes` : ""}. La
            operación es todo o nada: si alguna fila falla, no se importa ninguna. ¿Desea continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={() => setConfirmarOpen(false)}>
            Cancelar
          </MDButton>
          <MDButton variant="gradient" color="success" onClick={handleImportar}>
            Importar
          </MDButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
